import {
  type Author,
  type DocumentPermissions,
  type DocumentsPrivacyEnum,
  type Folder as APIFolder,
  type FolderPermissions,
  type StatusD10Enum,
  type DocumentPerms,
  type FolderList,
  DocumentListFolderItemTypeEnum,
  FolderWithPermissionsFolderItemTypeEnum,
} from "@outverse-video/outverse-api-client-lib";
import type { JSONDocument, JSONFolder } from "app/stores/documents_api";
import type { Readable, Writable } from "svelte/store";

export enum DocumentTypeEnum {
  Document = "document",
  CustomSpace = "custom-space",
  Snippet = "snippet",
}

export type Document = {
  uuid: string;
  title: string;
  privacy: DocumentsPrivacyEnum;
  status: StatusD10Enum;
  isPublished: boolean;
  isDraft: boolean;
  permissions: Array<DocumentPerms>;
  myPermissions: DocumentPermissions;
  authors: Array<Author>;
  folder: FolderList;
  content: {
    crdt: Uint8Array;
    json: { [key: string]: any };
    text: string;
    title: string;
  };
  created: Date;
  modified?: Date;
  draftUpdatedAt: Date;
  publishedAt?: Date;
  createdBy: Author;
  modifiedBy: Author;
  draftUpdatedBy: Author;
  favourite: boolean;
  icon?: string | null;
  iconDark?: string | null;
  latestVersionId: number;
  latestVersionAt: Date;
  latestVersionBy: Author;
  docType?: DocumentTypeEnum;
};

export interface Folder extends Omit<APIFolder, "items"> {
  parentUuid?: string;
  items: (WritableDocument | WritableFolder)[];
  myPermissions: FolderPermissions;
}

export interface WritableDocument extends Writable<Document> {
  uuid: string;
  waitForSave: (fn: CallableFunction) => Promise<void>;
  parentSection: Readable<Folder | undefined>;
  folderItemType: DocumentListFolderItemTypeEnum;
  updateFromJson: (json: Partial<JSONDocument>) => void;
  setStatus: (status: StatusD10Enum) => void;
  setAuthors: (authors: Author[]) => void;
  toggleStatus: () => StatusD10Enum;
  toggleFavourite: () => Promise<boolean>;
  delete: () => void;
  saveDraft: (content: Pick<Document, "content">, draftJson: Record<string, any>) => void;
  move: (folder: Folder) => void;
  moveAfterDragAndDrop: (index: number, folder: Folder) => void;
  getNextDoc: () => Document | undefined;
  getPreviousDoc: () => Document | undefined;
}

export interface WritableFolder extends Writable<Folder> {
  uuid: string;
  depth: number;
  folderItemType: FolderWithPermissionsFolderItemTypeEnum;
  updateFromJson: (json: JSONFolder, depth: number) => void;
  removeItem: (uuid: string) => void;
  addItem: (doc: WritableDocument | WritableFolder) => void;
  delete: () => void;
  move: (folder: Folder) => void;
  moveAfterDragAndDrop: (index: number, folder?: Folder) => void;
  getAllSubFolders: () => WritableFolder[];
  docs: Readable<WritableDocument[]>;
  folders: Readable<WritableFolder[]>;
}

export function isNestedDocument(
  folderItem: WritableDocument | WritableFolder
): folderItem is WritableDocument {
  return folderItem.folderItemType === DocumentListFolderItemTypeEnum.NestedDocument;
}
